import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Inicio from "./components/paginas/Inicio";
import Cocina from "./components/paginas/Cocina";
import Ingredientes from "./components/paginas/Ingredientes";
import Stock from "./components/paginas/Stock";
import Menu from "./components/paginas/Menu";
import Promos from "./components/paginas/Promos";
import Pedidos from "./components/paginas/Pedidos";
import MozoAdmin from "./components/paginas/MozoAdmin";
import Pagos from "./components/paginas/Pagos";
import Estadisticas from "./components/paginas/Estadisticas";
import MiRestaurante from "./components/paginas/MiRestaurante";
import MercadoPagoConnectCallback from "./components/paginas/MercadoPagoConnectCallback";
import Configuracion from "./components/paginas/Configuracion";
import Personal from "./components/paginas/Personal";
import DisplayPedidos from "./components/paginas/DisplayPedidos";
import CocinaPublica from "./components/paginas/CocinaPublica";
import QrMeza from "./components/paginas/QrMeza";
import MenuComensalQR from "./components/paginas/MenuComensalQr";
import DeliveryPublic from "./components/paginas/DeliveryPublic";
import WePedidos, { WePedidosOrders, WePedidosTracking } from "./components/paginas/WePedidos";
import WeCustomerAuthCallback from "./components/we-pedidos/WeCustomerAuthCallback";
import WePedidosMerchantEntry from "./components/we-pedidos/WePedidosMerchantEntry";
import { isDeliveryOnly } from "./services/local/merchantExperience";
import { getRestaurantProfile } from "./services/local/restaurantProfile";
import WePedidosAvailabilityGate from "./components/we-pedidos/WePedidosAvailabilityGate";
import WeRider from "./components/paginas/WeRider";
import WeRiderAuthCallback from "./components/we-pedidos/WeRiderAuthCallback";
import WeRiderMercadoPagoCallback from "./components/we-pedidos/WeRiderMercadoPagoCallback";
import TotemKiosk from "./components/paginas/TotemKiosk";
import KioskAndroidLanding from "./components/paginas/KioskAndroidLanding";
import KioskPaymentLanding from "./components/paginas/KioskPaymentLanding";
import PoliticaPrivacidad from "./components/paginas/PoliticaPrivacidad";
import TerminosUso from "./components/paginas/TerminosUso";
import EliminacionDatos from "./components/paginas/EliminacionDatos";
import BackofficeShell from "./components/common/BackofficeShell";
import LayoutRestaurante from "./components/common/LayoutRestaurante";
import ProtectedRoute from "./components/common/ProtectedRoute";
import ModuleRoute from "./components/common/ModuleRoute";
import RuntimeEnvironmentGate from "./components/common/RuntimeEnvironmentGate";
import LocalPersonnelTestSessionBar from "./components/common/LocalPersonnelTestSessionBar";
import AuthRestaurante from "./components/paginas/AuthRestaurante";
import Colaboradores from "./components/paginas/Colaboradores";
import RecuperarContrasena from "./components/paginas/RecuperarContrasena";
import SeleccionAcceso from "./components/paginas/SeleccionAcceso";
import PanelMozo, { PanelMozoStandaloneDemo } from "./components/paginas/PanelMozo";
import { AuthProvider, useAuth } from "./components/context/AuthContext";
import {
  buildKioskLauncherPath,
  isStandaloneAppDisplay,
  loadKioskAndroidLauncherConfig,
} from "./services/local/kioskLauncher";
import { migrateStorageNamespaces } from "./services/local/storageNamespace";
import { applyInstitutionalBrandingAssets } from "./services/local/institutionalBranding";
import { installServiceNotificationAudioUnlock } from "./services/local/serviceNotifications";
import { isInternalBackofficeHost } from "./services/local/runtimeHost";
import "./index.css";

const Backoffice = lazy(() => import("./components/paginas/Backoffice"));
const PrepararRestaurante = lazy(() => import("./components/paginas/PrepararRestaurante"));
const Ventas = lazy(() => import("./components/paginas/Ventas"));
const WePedidosMerchantHome = lazy(() => import("./components/we-pedidos/WePedidosMerchantHome"));

function AppIndexRoute() {
  const { canAccessModule, currentBranch, currentRestaurant } = useAuth();

  if (canAccessModule("inicio")) {
    if (isDeliveryOnly(getRestaurantProfile(currentBranch || currentRestaurant))) {
      return <Navigate to="/app/comercios" replace />;
    }
    return <Inicio />;
  }

  const fallbackRoute =
    (canAccessModule("cocina") && "/app/cocina") ||
    (canAccessModule("menu") && "/app/menu") ||
    (canAccessModule("promos") && "/app/promos") ||
    (canAccessModule("pedidos") && "/app/pedidos") ||
    (canAccessModule("mozos") && "/app/mozos") ||
    (canAccessModule("stock") && "/app/stock") ||
    (canAccessModule("restaurante") && "/app/restaurante") ||
    (canAccessModule("qr") && "/app/qr") ||
    (canAccessModule("pagos") && "/app/pagos") ||
    (canAccessModule("estadisticas") && "/app/estadisticas") ||
    (canAccessModule("personal") && "/app/personal") ||
    (canAccessModule("configuracion") && "/app/configuracion") ||
    "/acceso";

  return <Navigate to={fallbackRoute} replace />;
}

function RootEntryRoute() {
  const searchParams = new URLSearchParams(window.location.search);
  const mesaQrToken = String(searchParams.get("mesaQr") || "").trim();
  const kioskBranchId = String(searchParams.get("totemBranch") || "").trim();
  const kioskStationNumber = String(searchParams.get("totemStation") || "").trim();
  const kioskSetupMode = String(searchParams.get("totemSetup") || "").trim() === "1";
  const kioskAndroidLauncherConfig = loadKioskAndroidLauncherConfig();

  if (mesaQrToken) {
    return <Navigate to={`/mesa/${mesaQrToken}`} replace />;
  }

  if (kioskBranchId) {
    const safeStation = kioskStationNumber
      ? `/totem/${kioskBranchId}/${kioskStationNumber}`
      : `/totem/${kioskBranchId}`;
    const safeSearch = !kioskStationNumber && kioskSetupMode ? "?setup=1" : "";

    return <Navigate to={`${safeStation}${safeSearch}`} replace />;
  }

  if (isStandaloneAppDisplay() && kioskAndroidLauncherConfig?.branchId) {
    const launcherPath = buildKioskLauncherPath(kioskAndroidLauncherConfig);

    if (launcherPath) {
      return <Navigate to={`${launcherPath}?launcher=android`} replace />;
    }
  }

  if (isInternalBackofficeHost()) {
    return <Navigate to="/backoffice" replace />;
  }

  return <Navigate to="/login" replace />;
}

function RestaurantAppRoute() {
  if (isInternalBackofficeHost()) {
    return <Navigate to="/backoffice" replace />;
  }

  return (
    <ProtectedRoute>
      <LayoutRestaurante />
    </ProtectedRoute>
  );
}

function BackofficeLoadingFallback() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        backgroundColor: "#f8fafc",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "460px",
          padding: "28px",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
          textAlign: "center",
        }}
      >
        <h2 style={{ marginTop: 0 }}>Cargando Backoffice</h2>
        <p style={{ marginBottom: 0, color: "#475569" }}>
          Estamos preparando el panel interno de MozzoApp.
        </p>
      </div>
    </div>
  );
}

function CocinaEntryRoute() {
  const { isCreatorMode, isKitchenWorkspace, isWaiterDemoMode, loadingAuth } = useAuth();
  const searchParams =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const publicCode = String(searchParams?.get("code") || "").trim();

  if (loadingAuth) {
    return (
      <ProtectedRoute kitchenOnly>
        <Cocina />
      </ProtectedRoute>
    );
  }

  const shouldOpenPublicKitchen =
    Boolean(publicCode) ||
    (!isCreatorMode && !isWaiterDemoMode && !isKitchenWorkspace);

  if (shouldOpenPublicKitchen) {
    return <CocinaPublica />;
  }

  return (
    <ProtectedRoute kitchenOnly>
      <Cocina />
    </ProtectedRoute>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));

migrateStorageNamespaces();
applyInstitutionalBrandingAssets();
installServiceNotificationAudioUnlock();

root.render(
  <React.StrictMode>
    <RuntimeEnvironmentGate>
      <AuthProvider>
        <BrowserRouter>
          <LocalPersonnelTestSessionBar />
          <Routes>
          <Route path="/" element={<RootEntryRoute />} />
          <Route path="/politica-de-privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/privacy-policy" element={<PoliticaPrivacidad />} />
          <Route path="/terminos-de-uso" element={<TerminosUso />} />
          <Route path="/terms" element={<TerminosUso />} />
          <Route path="/eliminacion-de-datos" element={<EliminacionDatos />} />
          <Route path="/kiosk" element={<KioskAndroidLanding />} />
          <Route path="/kiosk/pago" element={<KioskPaymentLanding />} />
          <Route path="/mesa/:qrToken" element={<MenuComensalQR />} />
          <Route path="/delivery/:deliveryToken" element={<WePedidosAvailabilityGate deliveryEntryOnly><DeliveryPublic /></WePedidosAvailabilityGate>} />
          <Route path="/we-pedidos" element={<WePedidosAvailabilityGate><WePedidos /></WePedidosAvailabilityGate>} />
          <Route path="/we-pedidos/comercios" element={<WePedidosMerchantEntry />} />
          <Route path="/we-pedidos/pedidos" element={<WePedidosOrders />} />
          <Route path="/we-pedidos/auth/callback" element={<WeCustomerAuthCallback />} />
          <Route path="/we-pedidos/seguimiento/:trackingToken" element={<WePedidosTracking />} />
          <Route path="/we-pedidos/rider" element={<WeRider />} />
          <Route path="/we-pedidos/rider/auth/callback" element={<WeRiderAuthCallback />} />
          <Route
            path="/we-pedidos/rider/mercado-pago/callback"
            element={<WeRiderMercadoPagoCallback />}
          />
          <Route path="/display" element={<DisplayPedidos />} />
          <Route path="/totem/:branchId" element={<TotemKiosk />} />
          <Route path="/totem/:branchId/:stationNumber" element={<TotemKiosk />} />
          <Route path="/mozo-demo" element={<PanelMozoStandaloneDemo />} />
          <Route path="/login" element={<AuthRestaurante />} />
          <Route path="/colaboradores/*" element={<Colaboradores />} />
          <Route
            path="/recuperar-contrasena"
            element={<RecuperarContrasena />}
          />
          <Route
            path="/acceso"
            element={
              <ProtectedRoute allowAccessSelection>
                <SeleccionAcceso />
              </ProtectedRoute>
            }
          />
          <Route
            path="/backoffice"
            element={
              <ProtectedRoute allowPlatformBackoffice>
                <BackofficeShell>
                  <Suspense fallback={<BackofficeLoadingFallback />}>
                    <Backoffice />
                  </Suspense>
                </BackofficeShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mozo"
            element={
              <ProtectedRoute waiterOnly>
                <PanelMozo />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cocina"
            element={<CocinaEntryRoute />}
          />
          <Route
            path="/app/cobros/mercado-pago/callback"
            element={<MercadoPagoConnectCallback />}
          />

          <Route
            path="/app"
            element={<RestaurantAppRoute />}
          >
            <Route index element={<AppIndexRoute />} />
            <Route path="comercios" element={<ModuleRoute moduleKey="inicio"><Suspense fallback={<p>Abriendo tu comercio...</p>}><WePedidosMerchantHome /></Suspense></ModuleRoute>} />
            <Route path="preparar" element={<Suspense fallback={<p>Abriendo preparacion...</p>}><PrepararRestaurante /></Suspense>} />
            <Route
              path="cocina"
              element={
                <ModuleRoute
                  moduleKey="cocina"
                  title="Cocina restringida"
                  description="Esta cuenta no tiene permiso para operar la pantalla de cocina de la sucursal activa."
                >
                  <Cocina />
                </ModuleRoute>
              }
            />
            <Route
              path="mozos"
              element={
                <ModuleRoute
                  moduleKey="mozos"
                  title="Mozo restringido"
                  description="Esta cuenta no tiene permiso para administrar el equipo de salon ni las opciones del boton mozo."
                >
                  <MozoAdmin />
                </ModuleRoute>
              }
            />
            <Route
              path="ingredientes"
              element={
                <ModuleRoute
                  moduleKey="ingredientes"
                  title="Ingredientes restringidos"
                  description="Esta cuenta no tiene permiso para editar ingredientes en la sucursal activa."
                >
                  <Ingredientes />
                </ModuleRoute>
              }
            />
            <Route
              path="stock"
              element={
                <ModuleRoute
                  moduleKey="stock"
                  title="Stock restringido"
                  description="Esta cuenta no tiene permiso para administrar stock en la sucursal activa."
                >
                  <Stock />
                </ModuleRoute>
              }
            />
            <Route
              path="menu"
              element={
                <ModuleRoute
                  moduleKey="menu"
                  title="Menu restringido"
                  description="Esta cuenta no tiene permiso para modificar el menu de la sucursal activa."
                >
                  <Menu />
                </ModuleRoute>
              }
            />
            <Route
              path="promos"
              element={
                <ModuleRoute
                  moduleKey="promos"
                  title="Promos restringidas"
                  description="Esta cuenta no tiene permiso para administrar combos y promociones en la sucursal activa."
                >
                  <Promos />
                </ModuleRoute>
              }
            />
            <Route
              path="pedidos"
              element={
                <ModuleRoute
                  moduleKey="pedidos"
                  title="Delivery restringido"
                  description="Esta cuenta no tiene permiso para administrar pedidos externos, clientes ni delivery."
                >
                  <Pedidos />
                </ModuleRoute>
              }
            />
            <Route
              path="ventas"
              element={
                <ModuleRoute moduleKey="pagos" title="Ventas restringidas">
                  <Suspense fallback={<p>Cargando Ventas...</p>}><Ventas /></Suspense>
                </ModuleRoute>
              }
            />
            <Route
              path="pagos"
              element={
                <ModuleRoute
                  moduleKey="pagos"
                  title="Caja restringida"
                  description="Esta cuenta no tiene permiso para entrar al sector de caja."
                >
                  <Pagos />
                </ModuleRoute>
              }
            />
            <Route
              path="pagos/mz"
              element={
                <ModuleRoute
                  moduleKey="pagos"
                  title="Compra de MZ restringida"
                  description="Esta cuenta no tiene permiso para entrar al sector de caja y administrar creditos MZ."
                >
                  <Pagos />
                </ModuleRoute>
              }
            />
            <Route
              path="estadisticas"
              element={
                <ModuleRoute
                  moduleKey="estadisticas"
                  title="Estadisticas restringidas"
                  description="Esta cuenta no tiene permiso para entrar al panel de estadisticas."
                >
                  <Estadisticas />
                </ModuleRoute>
              }
            />
            <Route
              path="restaurante"
              element={
                <ModuleRoute
                  moduleKey="restaurante"
                  title="Mi Restaurante restringido"
                  description="Esta cuenta no tiene permiso para editar horarios, datos visibles ni configuracion operativa de la sucursal."
                >
                  <MiRestaurante />
                </ModuleRoute>
              }
            />
            <Route
              path="configuracion"
              element={
                <ModuleRoute
                  moduleKey="configuracion"
                  title="Configuracion solo para admin general"
                  description="La administracion de sucursales, usuarios y turnos queda reservada al admin general."
                >
                  <Configuracion />
                </ModuleRoute>
              }
            />
            <Route
              path="personal"
              element={
                <ModuleRoute
                  moduleKey="personal"
                  title="Personal restringido"
                  description="Esta cuenta no tiene permiso para gestionar personal dentro de la sucursal activa."
                >
                  <Personal />
                </ModuleRoute>
              }
            />
            <Route
              path="qr"
              element={
                <ModuleRoute
                  moduleKey="qr"
                  title="QR restringido"
                  description="Esta cuenta no tiene permiso para gestionar QR por mesa en la sucursal activa."
                >
                  <QrMeza />
                </ModuleRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </RuntimeEnvironmentGate>
  </React.StrictMode>
);
